import ReactDOM from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import store from './store';

import { OrgDashboard } from './routes/org/dashboard';
import { OrgDashboardAttendeesBySlug } from './routes/org/dashboard/attendees';
import { OrgEvent } from './routes/org/event';
import OrgEventNewCheckout from './routes/org/event/checkout/slug';
import { OrgEventNewCustomPlan } from './routes/org/event/new/custom-plan/root';
import { OrgEventNewLayout } from './routes/org/event/new/layout';
import { OrgEventNewStep1 } from './routes/org/event/new/step-1';
import { OrgEventNewStep2 } from './routes/org/event/new/step-2';
import { OrgEventNewStep3 } from './routes/org/event/new/step-3';
import { OrgEventDetailAttendee } from './routes/org/event/slug/attendee';
import { OrgEventDetailInfo } from './routes/org/event/slug/info';
import { OrgEventDetailLayout } from './routes/org/event/slug/layout';
import { OrgEventDetailTransaction } from './routes/org/event/slug/transaction';
import { OrgEventDetailVouchersDetail } from './routes/org/event/slug/voucher/id';
import { OrgLayout } from './routes/org/layout';
import { OrgTransaction, OrgTransactionDetail } from './routes/org/transaction';

import NotFound from '@/routes/error/404';
import { AuthLayout } from '@/routes/org/auth';
import { Complete } from '@/routes/org/auth/complete';
import { AuthCreateEvent } from '@/routes/org/auth/event/create';
import { Login } from '@/routes/org/auth/login';
import { SendLinkResetPassword } from '@/routes/org/auth/password/email';
import { ResetPassword } from '@/routes/org/auth/password/reset';
import { Register } from '@/routes/org/auth/register';
import { OrgEventNewStep4 } from '@/routes/org/event/new/step-4';
import { OrgCommerceBanks } from '@/routes/org/event/slug/commerce/banks/root';
import { OrgCommerceLayout } from '@/routes/org/event/slug/commerce/layout';
import { OrgCommercePayment } from '@/routes/org/event/slug/commerce/payment/root';
import { OrgCommerceWithdrawal } from '@/routes/org/event/slug/commerce/withdrawal/root';
import { OrgEventAffiliator } from '@/routes/org/event/slug/marketing/affiliator';
import { OrgEventAnnouncement } from '@/routes/org/event/slug/marketing/announcement';
import { OrgEventAnnouncementRecipient } from '@/routes/org/event/slug/marketing/announcement/recipient';
import { OrgEventMarketingLayout } from '@/routes/org/event/slug/marketing/layout';
import { OrgEventSocialMedia } from '@/routes/org/event/slug/marketing/social';
import { OrgOtsSystemLayout } from '@/routes/org/event/slug/ots/layout';
import { OrgEventCommittee } from '@/routes/org/event/slug/settings/committee';
import { OrgEventSettingLayout } from '@/routes/org/event/slug/settings/layout';
import { OrgEventPermit } from '@/routes/org/event/slug/settings/permits';
import { OrgTicketList } from '@/routes/org/event/slug/ticket';
import { OrgEventDetailVouchers } from '@/routes/org/event/slug/voucher';
import { OrgProfileLayout } from '@/routes/org/profile';
import { OrgSettingBillsLayout } from '@/routes/org/profile/bills/layout';
import { OrgSettingBillsPayment } from '@/routes/org/profile/bills/transaction/root';
import { OrgSettingCommerceLayout } from '@/routes/org/profile/commerce/layout';
import { OrgSettingCommercePayment } from '@/routes/org/profile/commerce/payment';
import { OrgSettingWithdrawal } from '@/routes/org/profile/commerce/withdrawal/root';
import { OrgProfile } from '@/routes/org/profile/info';
import { OrgProfileSecurity } from '@/routes/org/profile/security';
import { OrgSettingWebsite } from '@/routes/org/profile/website/root';
import { QrCodeDashboard } from '@/routes/org/qrcode';
import { configureEcho } from '@laravel/echo-react';
import { Toaster } from 'react-hot-toast';
import { Provider } from 'react-redux';
import './index.css';
import ExternalRedirect from './routes/root';

const router = createBrowserRouter([
  {
    path: 'ots-system/tickets/:id',
    element: <ExternalRedirect />,
  },
  {
    path: 'auth',
    element: <AuthLayout />,
    children: [
      {
        path: 'login',
        element: <Login />,
      },
      {
        path: 'reset-password',
        element: <ResetPassword />,
      },
      {
        path: 'reset-password/send-email',
        element: <SendLinkResetPassword />,
      },
      {
        path: 'register',
        element: <Register />,
      },
      {
        path: 'complete',
        element: <Complete />,
      },
      {
        path: 'event',
        element: <AuthCreateEvent />,
      },
    ],
  },
  {
    path: '/',
    element: <OrgLayout />,
    children: [
      {
        path: 'dashboard',
        index: true,
        element: <OrgDashboard />,
      },
      {
        path: 'dashboard/attendees/:slug',
        element: <OrgDashboardAttendeesBySlug />,
      },
      {
        path: 'transaction',
        element: <OrgTransaction />,
      },
      {
        path: 'transaction/:id',
        element: <OrgTransactionDetail />,
      },
      {
        path: 'event',
        element: <OrgEvent />,
      },
      {
        path: 'event/:slug/edit',
        element: <OrgEventNewLayout />,
        children: [
          {
            path: 'step-1',
            element: <OrgEventNewStep1 />,
          },
          {
            path: 'step-2',
            element: <OrgEventNewStep2 />,
          },
          {
            path: 'step-3',
            element: <OrgEventNewStep3 />,
          },
          {
            path: 'step-4',
            element: <OrgEventNewStep4 />,
          },
        ],
      },
      {
        path: 'event/new/custom-plan',
        element: <OrgEventNewCustomPlan />,
      },
      {
        path: 'event/new/checkout/:slug',
        element: <OrgEventNewCheckout />,
      },
      {
        path: 'event/:slug',
        element: <OrgEventDetailLayout />,
        children: [
          {
            path: 'info',
            element: <OrgEventDetailInfo />,
          },
          {
            path: 'ots',
            element: <OrgOtsSystemLayout />,
          },
          {
            path: 'ticket',
            element: <OrgTicketList />,
          },
          {
            path: 'transaction',
            element: <OrgEventDetailTransaction />,
          },
          {
            path: 'attendee',
            element: <OrgEventDetailAttendee />,
          },
          {
            path: 'attendee/qrcode',
            element: <QrCodeDashboard />,
          },
          {
            path: 'commerce',
            element: <OrgCommerceLayout />,
            children: [
              {
                path: 'payment',
                element: <OrgCommercePayment />,
              },
              {
                path: 'withdrawal',
                element: <OrgCommerceWithdrawal />,
              },
              {
                path: 'banks',
                element: <OrgCommerceBanks />,
              },
            ],
          },
          {
            path: 'marketing',
            element: <OrgEventMarketingLayout />,
            children: [
              {
                path: 'voucher',
                element: <OrgEventDetailVouchers />,
              },
              {
                path: 'affiliator',
                element: <OrgEventAffiliator />,
              },
              {
                path: 'social-media',
                element: <OrgEventSocialMedia />,
              },
              {
                path: 'announcement',
                element: <OrgEventAnnouncement />,
              },
              {
                path: 'announcement/:uuid/recipients',
                element: <OrgEventAnnouncementRecipient />,
              },
            ],
          },
          {
            path: 'settings',
            element: <OrgEventSettingLayout />,
            children: [
              {
                path: 'committee',
                element: <OrgEventCommittee />,
              },
              {
                path: 'permit',
                element: <OrgEventPermit />,
              },
            ],
          },
        ],
      },
      {
        path: 'event/:slug/voucher/:id',
        element: <OrgEventDetailVouchersDetail />,
      },
      {
        path: 'setting',
        element: <OrgProfileLayout />,
        children: [
          {
            path: 'info',
            element: <OrgProfile />,
          },
          {
            path: 'bills',
            element: <OrgSettingBillsLayout />,
            children: [
              {
                path: 'transactions',
                element: <OrgSettingBillsPayment />,
              },
            ],
          },
          {
            path: 'website',
            element: <OrgSettingWebsite />,
          },
          {
            path: 'commerce',
            element: <OrgSettingCommerceLayout />,
            children: [
              {
                path: 'providers',
                element: <OrgSettingCommercePayment />,
              },
              {
                path: 'withdraws',
                element: <OrgSettingWithdrawal />,
              },
            ],
          },
          {
            path: 'security',
            element: <OrgProfileSecurity />,
          },
        ],
      },
      {
        path: '*',
        element: <NotFound />,
      },
    ],
  },
]);

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

configureEcho({
  broadcaster: 'pusher',
  key: process.env.REACT_APP_SOCKET_KEY,
  cluster: process.env.REACT_APP_SOCKET_CLUSTER,
  wsHost: process.env.REACT_APP_SOCKET_HOST,
  wsPort: process.env.REACT_APP_SOCKET_PORT,
  wssPort: process.env.REACT_APP_SOCKET_PORT,
  forceTLS: (process.env.REACT_APP_SOCKET_TLS ?? 'https') === 'https',
  enabledTransports: ['ws', 'wss'],
  /** @ts-ignore */
  authEndpoint: process.env.REACT_APP_BASE_URL + '/broadcasting/auth',
});

root.render(
  <Provider store={store}>
    <RouterProvider router={router} />
    <Toaster />
  </Provider>
);

// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
// reportWebVitals(console.log);
